import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, X, Sparkles, MapPin, Users } from 'lucide-react';
import { SCShell, SCHeading, goldBtn, ghostBtn, RequireAuth } from './sc/SCShared';
import { scDiscover, scLike, scFileUrl } from '../api';

const Card = ({ p, onAct }) => {
  const m = p.match || {};
  return (
    <div className="rounded-2xl overflow-hidden border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md" data-testid={`sc-card-${p.user_id}`}>
      <div className="h-56 bg-black/40 relative">
        {p.profile_photo
          ? <img src={scFileUrl(p.profile_photo)} alt={p.display_name} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><span className="font-cinzel text-5xl text-[#8b5cf6]/50">{(p.display_name || '?')[0]}</span></div>}
        <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <h3 className="font-cinzel text-xl text-[#f5d786]">{p.display_name}{p.age ? `, ${p.age}` : ''}</h3>
          {p.location_area && <p className="font-jost text-xs text-[#e9d8ff]/70 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location_area}</p>}
        </div>
      </div>
      <div className="p-5">
        {p.relationship_intention && <p className="font-jost text-xs text-[#d946ef] uppercase tracking-widest mb-2">{p.relationship_intention}</p>}
        {p.bio && <p className="font-jost text-sm text-[#e9d8ff]/70 mb-3 line-clamp-3">{p.bio}</p>}
        <div className="flex items-center gap-2 text-[#e9d8ff]/80 mb-2"><Users className="h-4 w-4 text-[#8b5cf6]" /><span className="font-jost text-sm">{m.shared_count || 0} shared interest{(m.shared_count || 0) === 1 ? '' : 's'}</span></div>
        {(m.shared_spiritual?.length || m.shared_lifestyle?.length) ? (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {[...(m.shared_spiritual || []), ...(m.shared_lifestyle || [])].slice(0, 6).map((s) => (
              <span key={s} className="text-[11px] px-2.5 py-1 rounded-full bg-[#8b5cf6]/15 border border-[#8b5cf6]/30 text-[#e9d8ff]/80">{s}</span>
            ))}
          </div>
        ) : null}
        {m.compatible?.length ? <p className="font-jost text-xs text-[#a7f3d0]/80 mb-1">✓ {m.compatible.join(' · ')}</p> : null}
        {m.different?.length ? <p className="font-jost text-xs text-[#e9d8ff]/45">Different: {m.different.join(' · ')}</p> : null}
        <div className="flex gap-3 mt-4">
          <button onClick={() => onAct(p.user_id, 'pass')} data-testid={`sc-pass-${p.user_id}`} className="flex-1 rounded-full py-2.5 border border-[#8b5cf6]/40 text-[#e9d8ff]/80 hover:bg-white/5 flex items-center justify-center gap-2 text-sm"><X className="h-4 w-4" /> Pass</button>
          <button onClick={() => onAct(p.user_id, 'like')} data-testid={`sc-like-${p.user_id}`} className="flex-1 rounded-full py-2.5 bg-gradient-to-r from-[#7c3aed] to-[#d946ef] text-white flex items-center justify-center gap-2 text-sm"><Heart className="h-4 w-4" /> Like</button>
        </div>
      </div>
    </div>
  );
};

const Inner = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [done, setDone] = useState(false);

  const load = (s = 0) => {
    setLoading(true);
    scDiscover(s, 12).then((d) => {
      setProfiles((prev) => s === 0 ? d.profiles : [...prev, ...d.profiles]);
      if (d.profiles.length < 12) setDone(true);
      setSkip(s + d.profiles.length);
    }).catch((e) => {
      if (e?.response?.status === 403) toast.error('Activate your profile first.');
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

  const act = async (uid, action) => {
    setProfiles((prev) => prev.filter((x) => x.user_id !== uid));
    try {
      const r = await scLike(uid, action);
      if (r.matched) toast.success("It's a connection! Find them in your Matches.", { icon: '✨' });
    } catch { toast.error('Something went wrong.'); }
  };

  return (
    <SCShell>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <SCHeading eyebrow="Discover" title="Find your resonance" />
        <div className="flex gap-3">
          <Link to="/sacred-connections/matches" className={ghostBtn}>My Matches</Link>
          <Link to="/sacred-connections/profile" className={ghostBtn}>My Profile</Link>
        </div>
      </div>
      {profiles.length === 0 && !loading ? (
        <div className="text-center py-20">
          <Sparkles className="h-10 w-10 text-[#8b5cf6] mx-auto mb-4" />
          <p className="font-cormorant text-2xl text-[#e9d8ff]/70">No new profiles right now.</p>
          <p className="font-jost text-sm text-[#e9d8ff]/50 mt-2">Check back soon as more members join, or refine your profile.</p>
          <Link to="/sacred-connections/profile" className={`${goldBtn} mt-6`}>Complete your profile</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sc-discover-grid">
          {profiles.map((p) => <Card key={p.user_id} p={p} onAct={act} />)}
        </div>
      )}
      {!done && profiles.length > 0 && (
        <div className="text-center mt-10"><button onClick={() => load(skip)} disabled={loading} className={ghostBtn} data-testid="sc-load-more">{loading ? 'Loading…' : 'Show more'}</button></div>
      )}
    </SCShell>
  );
};

const SCDiscover = () => <RequireAuth><Inner /></RequireAuth>;
export default SCDiscover;
